import { supabase } from './supabase';
import { LeaveRequest } from '../types';
import { leaveBalanceService } from './leaveBalanceService';
import { enhancedLeaveRequestService } from './enhancedLeaveRequestService';

export const leaveRequestService = {
  async getAll(): Promise<LeaveRequest[]> {
    try {
      // Use enhanced service for better error handling and workarounds
      return await enhancedLeaveRequestService.getAll();
    } catch (error) {
      console.error('Error fetching leave requests:', error);
      return [];
    }
  },

  async getById(id: string): Promise<LeaveRequest | null> {
    try {
      return await enhancedLeaveRequestService.getById(id);
    } catch (error) {
      console.error('Error fetching leave request:', error);
      return null;
    }
  },

  async getByEmployeeId(employeeId: string): Promise<LeaveRequest[]> {
    try {
      return await enhancedLeaveRequestService.getByEmployeeId(employeeId);
    } catch (error) {
      console.error('Error fetching employee leave requests:', error);
      return [];
    }
  },

  async getByStatus(status: string): Promise<LeaveRequest[]> {
    try {
      return await enhancedLeaveRequestService.getByStatus(status);
    } catch (error) {
      console.error('Error fetching leave requests by status:', error);
      return [];
    }
  },

  async create(leaveRequest: Omit<LeaveRequest, 'id' | 'created_at' | 'updated_at'>): Promise<{
    leaveRequest: LeaveRequest;
    balanceCheck: { hasBalance: boolean; remainingDays: number; message: string };
  }> {
    try {
      // Calculate working days
      const workingDays = leaveBalanceService.calculateWorkingDays(
        leaveRequest.start_date,
        leaveRequest.end_date
      );

      // Check leave balance
      const year = new Date(leaveRequest.start_date).getFullYear();
      const balanceCheck = await leaveBalanceService.checkLeaveBalance(
        leaveRequest.employee_id,
        leaveRequest.leave_type,
        workingDays,
        year
      );

      // Determine if leave should be paid or unpaid
      const isPaid = balanceCheck.hasBalance && 
        ['vacation', 'sick', 'personal', 'emergency', 'maternity', 'paternity'].includes(leaveRequest.leave_type);

      // Use enhanced service to create the request
      const createdRequest = await enhancedLeaveRequestService.create({
        ...leaveRequest,
        is_paid: isPaid
      });

      if (!createdRequest) {
        throw new Error('Failed to create leave request');
      }

      return {
        leaveRequest: createdRequest,
        balanceCheck
      };
    } catch (error) {
      console.error('Error creating leave request:', error);
      throw error;
    }
  },

  async update(id: string, updates: Partial<LeaveRequest>): Promise<LeaveRequest> {
    try {
      const updatedRequest = await enhancedLeaveRequestService.update(id, updates);
      if (!updatedRequest) {
        throw new Error('Failed to update leave request');
      }
      return updatedRequest;
    } catch (error) {
      console.error('Error updating leave request:', error);
      throw error;
    }
  },

  // Delete a leave request
  async delete(id: string): Promise<boolean> {
    try {
      return await enhancedLeaveRequestService.delete(id);
    } catch (error) {
      console.error(`Error deleting leave request with ID ${id}:`, error);
      return false;
    }
  },

  async approve(id: string, approvedBy: string, daysApproved?: number): Promise<{
    leaveRequest: LeaveRequest;
    balanceUpdate: any;
  }> {
    try {
      // Use enhanced service for better error handling and workarounds
      const updatedRequest = await enhancedLeaveRequestService.approve(id, approvedBy, daysApproved);
      
      if (!updatedRequest) {
        throw new Error('Failed to approve leave request');
      }

      // Get updated balance for UI feedback
      const year = new Date(updatedRequest.start_date).getFullYear();
      const updatedBalance = await leaveBalanceService.getEmployeeLeaveBalance(
        updatedRequest.employee_id,
        year
      );

      return {
        leaveRequest: updatedRequest,
        balanceUpdate: updatedBalance
      };
    } catch (error) {
      console.error('Error approving leave request:', error);
      throw error;
    }
  },

  async reject(id: string, approvedBy: string, reason?: string): Promise<LeaveRequest> {
    try {
      // Use enhanced service for better error handling and workarounds
      const rejectedRequest = await enhancedLeaveRequestService.reject(id, approvedBy, reason);
      
      if (!rejectedRequest) {
        throw new Error('Failed to reject leave request');
      }

      return rejectedRequest;
    } catch (error) {
      console.error('Error rejecting leave request:', error);
      throw error;
    }
  },

  // Get leave requests for a specific date range
  async getByDateRange(startDate: string, endDate: string): Promise<LeaveRequest[]> {
    try {
      const { data, error } = await supabase
        .from('leave_requests')
        .select(`
          *,
          employees!inner(
            id,
            first_name,
            last_name,
            email,
            department,
            position
          )
        `)
        .eq('status', 'approved')
        .gte('start_date', startDate)
        .lte('end_date', endDate)
        .order('start_date');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching leave requests by date range:', error);
      throw error;
    }
  },

  // Check if employee is on leave for a specific date
  async isEmployeeOnLeave(employeeId: string, date: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('leave_requests')
        .select('id')
        .eq('employee_id', employeeId)
        .eq('status', 'approved')
        .lte('start_date', date)
        .gte('end_date', date)
        .limit(1);

      if (error) throw error;
      return (data && data.length > 0) || false;
    } catch (error) {
      console.error('Error checking if employee is on leave:', error);
      return false;
    }
  },

  // Get upcoming leave requests (next 30 days)
  async getUpcomingLeaves(): Promise<LeaveRequest[]> {
    try {
      const today = new Date().toISOString().split('T')[0];
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
      const endDate = thirtyDaysFromNow.toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('leave_requests')
        .select(`
          *,
          employees!inner(
            id,
            first_name,
            last_name,
            email,
            department,
            position
          )
        `)
        .eq('status', 'approved')
        .gte('start_date', today)
        .lte('start_date', endDate)
        .order('start_date');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching upcoming leaves:', error);
      throw error;
    }
  }
};